import React, { useRef, useEffect, useCallback, useMemo } from "react";
import "./MainContent.css";

import { useAppDispatch, useAppSelector } from "../../hooks/redux";
import WelcomeMessage from "../Welcome/WelcomeMessage";
import MessageList from "../Message/MessageBubble";
import ErrorMessage from "../ErrorMessage/ErrorMessage";
import { askInSession, startNewSession, clearError } from "../../store/slices/chatSlice";

interface MainContentProps {
  onScrollPosition?: (isAtBottom: boolean) => void;
}

const MainContent: React.FC<MainContentProps> = ({ onScrollPosition }) => {
  const { messages, currentSessionId, error, loading } = useAppSelector((state) => state.chat);
  const dispatch = useAppDispatch();

  const currentMessages = useMemo(() =>
    currentSessionId ? messages[currentSessionId] || [] : [],
    [currentSessionId, messages]);
  const hasMessages = currentMessages.length > 0;

  const handleTipClick = (question: string) => {
    if (!currentSessionId) {
      dispatch(startNewSession({ question }));
    } else {
      dispatch(
        askInSession({
          sessionId: currentSessionId,
          question: { question },
        })
      );
    }
  };

  const handleRetry = () => {
    // Clear error and allow user to try again
    dispatch(clearError());
  };

  const handleDismissError = () => {
    dispatch(clearError());
  };

  const mainRef = useRef<HTMLElement>(null);

  const handleScroll = useCallback(() => {
    const el = mainRef.current;
    if (!el || !onScrollPosition) return;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 50;
    onScrollPosition(atBottom);
  }, [onScrollPosition]);

  useEffect(() => {
    const el = mainRef.current;
    if (!el || !onScrollPosition) return;
    el.addEventListener("scroll", handleScroll, { passive: true });
    return () => el.removeEventListener("scroll", handleScroll);
  }, [handleScroll, onScrollPosition]);

  return (
    <main className="main-content" ref={mainRef}>
      <div className="main-container">
        {error && (
          <ErrorMessage 
            message={error} 
            onRetry={!loading ? handleRetry : undefined}
            onDismiss={handleDismissError}
            isTimeout={error.includes('timeout')}
          />
        )}
        {hasMessages ? (
          <MessageList messages={currentMessages} />
        ) : (
          <WelcomeMessage onTipClick={handleTipClick} />
        )}
      </div>
    </main>
  );
};

export default MainContent;
